import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { Link } from 'react-router-dom';
import voca from 'voca';
import { PRODUCTION, generateSurfaceUrl } from '../utils/environments';
import { snakeCaseToTitleCase } from '../utils/api_utils';
import CustomLoader from '../../features/common/CustomLoader';
import { loadOrganizationDetails } from '../utils/check-auth';
import ToggleButton from './ToggleButton';
import Swal from 'sweetalert2';

const PublishProjectModal = ({
  projectId,
  version,
  versions,
  selVersion,
  isPublishing,
  popoverOpen,
  toggle,
  handleVersionChange,
  restartApp,
  publishApp,
  projectDetail,
  actions,
}) => {
  const [debugModeState, setDebugModeState] = useState({});
  let { domainName, url, uuid, environments, seoName, projectType, debugMode = [] } = projectDetail;
  const urlToOpen = !domainName || domainName === url ? '' : `https://${domainName}`;
  environments = environments ? environments.filter((env) => env.envType !== 'PREVIEW') : [];
  useEffect(() => {
    if (environments && debugMode && debugMode.length > 0) {
      const initialState = environments.reduce((acc, env) => {
        acc[env.envType] = {
          isEnable: debugMode.includes(env.envType),
          environment: env.envType,
        };
        return acc;
      }, {});
      setDebugModeState(initialState);
    }
  }, [debugMode]);

  const handleDebugModeToggle = async (envType) => {
    const currentState = debugModeState[envType] ? debugModeState[envType].isEnable : false;
    const newState = !currentState;
    const heading = `Are you sure to ${
      newState ? 'enable' : 'disable'
    } debug mode for ${voca.titleCase(envType)}?`;
    const description = newState
      ? 'It will start displaying logs in the browser console window.'
      : 'It will stop displaying logs in the browser console window.';

    const result = await Swal.fire({
      title: heading,
      text: description,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newState ? '#3085d6' : '#d33',
      cancelButtonColor: '#757f91',
      confirmButtonText: `Yes, ${newState ? 'enable' : 'disable'} it!`,
    });

    if (result.isConfirmed) {
      const newStateObject = {
        isEnable: newState,
        environment: envType,
      };

      setDebugModeState({
        ...debugModeState,
        [envType]: newStateObject,
      });
      await actions.toggleDebugMode({ projectId, version, data: newStateObject });
    }
  };

  const generateProjectURL = (envType, domainName, seoName, link = false) => {
    let url = generateSurfaceUrl(envType, seoName);
    if (link) return url;
    return (
      <>
        <i className="wb webconnect-box-arrow-in-up-right" title="Open link in new tab"></i>
        <a
          className="icon-link"
          style={{ color: '#1F2041' }}
          href={url}
          target="_blank"
          title="Open link in new tab"
        >
          {url}
        </a>
      </>
    );
  };

  const subscription = loadOrganizationDetails('subscription');
  let message = '';
  let disable = false;
  let link = '';
  let linkMsg = '';
  if (!subscription || subscription === 'BUILDER_FREE') {
    disable = true;
    message = 'Please upgrade your account to enable this feature.';
    link = `${process.env.REACT_APP_ORGANIZATION}accounts`;
    linkMsg = 'Upgrade';
  }
  if (projectType === 'FREE' && subscription !== 'UNLIMITED') {
    disable = true;
    message = "You don't have cloud/hosting subscription. Please subscribe to enable this feature.";
    link = `${process.env.REACT_APP_ORGANIZATION}${projectId}/cloud-hosting`;
    linkMsg = 'Subscribe';
  }
  if (disable) {
    return (
      <Modal size="lg" isOpen={popoverOpen} toggle={toggle} backdrop="static" className="publish">
        <ModalHeader toggle={toggle} className="border-bottom">
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ width: '48rem' }}
          >
            <strong>Publish your app for the end users.</strong>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="row pt-2 pb-2">
            <div className="col-md-12 col-sm-12 text-center">
              <strong>{message}</strong>
              <br />
              <a href={link} className="btn btn-theme-submit btn-sm">
                <i className={'wb webconnect-paper-plane-fill'}></i> {linkMsg}
              </a>
            </div>
          </div>
        </ModalBody>
      </Modal>
    );
  } else {
    return (
      <Modal size="lg" isOpen={popoverOpen} toggle={toggle} backdrop="static" className="publish">
        <ModalHeader toggle={toggle} className="border-bottom">
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ width: '48rem' }}
          >
            <strong>Publish your app for the end users.</strong>
            <Link
              to={{
                pathname: `/deployment-setting/${version}/v/${projectId}/code-export`,
              }}
              className="btn btn-theme-submit"
              title="Code Export"
            >
              <i className="wb webconnect-cursor-fill"></i>&nbsp;Export Code
            </Link>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="row pt-2 pb-2">
            <div className="col-md-12 col-sm-12">
              <strong>Publish your app for the end users on specific environment.</strong>
              <table className="table table-stripped table-bordered table-hover table-sm">
                <thead>
                  <tr>
                    <th>Server</th>
                    <th>URL</th>
                    <th>Versions</th>
                    <th>Debug</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {environments.map((env) => (
                    <tr key={env.uuid}>
                      <td>{snakeCaseToTitleCase(env.envType)}</td>
                      <td>
                        <p>{generateProjectURL(env.envType, domainName, seoName)}</p>
                        {env.envType === PRODUCTION && urlToOpen && (
                          <p>
                            <i
                              className="wb webconnect-box-arrow-up-right"
                              title="Open link in new tab"
                            ></i>
                            <a
                              className="icon-link"
                              style={{ color: '#1F2041' }}
                              href={urlToOpen}
                              target="_blank"
                              title="Open link in new tab"
                            >
                              {voca.truncate(urlToOpen, 50)}
                            </a>
                          </p>
                        )}
                      </td>
                      <td>
                        <select
                          className="form-control"
                          value={selVersion[env.envType]}
                          onChange={(event) => handleVersionChange(event, env.envType)}
                        >
                          <option value="">Select Version</option>
                          {versions.map((version) => (
                            <option value={version.currentVersion} key={version.currentVersion}>
                              v{version.currentVersion}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <ToggleButton
                          name="enableDebugMode"
                          bgColor="drapcode-violet"
                          onChange={() => handleDebugModeToggle(env.envType)}
                          isChecked={
                            debugModeState[env.envType]
                              ? debugModeState[env.envType].isEnable
                              : false
                          }
                        ></ToggleButton>
                      </td>
                      <td>
                        <div className="d-flex justify-content-center my-3">
                          <button
                            type="button"
                            className="btn btn-theme-submit btn-sm"
                            onClick={() => {
                              window.open(
                                generateProjectURL(env.envType, domainName, seoName, true),
                              );
                            }}
                          >
                            <i className="wb webconnect-eye-fill"></i>&nbsp;View
                          </button>
                          &nbsp;
                          <button
                            type="button"
                            className="btn btn-theme-submit btn-sm"
                            onClick={() => publishApp(env.envType, env.name)}
                            disabled={isPublishing}
                          >
                            <i className="wb webconnect-paper-plane-fill"></i>&nbsp;Publish
                            <CustomLoader loading={isPublishing} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </ModalBody>
      </Modal>
    );
  }
};

export default PublishProjectModal;
